import React, {
  useState,
  useContext,
  useMemo,
  useCallback,
  useEffect,
} from 'react';
import { observer } from 'mobx-react';
import { compose } from 'recompose';
import PropTypes from 'prop-types';
import moment from 'moment';
import format from 'string-template';
import { Checkbox, TextField } from '@mui/material';
import { CheckCircleOutline } from '@mui/icons-material';
import * as Sentry from '@sentry/browser';

import { ScheduledAssignmentType } from '../../../../../Model/ScheduledAssignment';
import ExternalCoachContext from '../../../../context/ExternalCoachContext';
import ManageTrackContext from '../../../../context/ManageTrackContext';
import useSessionStore from '../../../../../hooks/useSessionStore';
import Program from '../../../../../Model/Program';
import DialogRoundedModal from '../../../../../components/DialogRoundedModal';
import { SaveButton, CancelButton } from '../../../../../components/Button/ActionButtons';
import LoadingOverlay from '../../../../components/LoadingOverlay';
import useComponentMounted from '../../../../../hooks/useComponentMounted';
import useToast from '../../../../hooks/useToast';
import useScheduleTimeLabel from '../../../../hooks/useScheduleTimeLabel';
import { DateFormat } from '../../../../../utils/date';
import FirebaseContext from '../../../../../context/FirebaseContext';

import QAPContext from '../../../../../context/QAPContext';
import AssignmentProductSelector from '../../../../components/AssignmentProductSelector';
import {
  DialogBody,
  StyledAutocomplete,
  StyledOption,
  RowContainer,
  ColumnContainer,
  CheckBoxContainer,
  StyledCheckBoxLabel,
  SettingSection,
  StyledInputLabel,
  DurationContainer,
  StyledDurationText,
} from './styles';
import texts from './texts.json';

const TrackAssignmentModal = ({
  showModal,
  onClose,
  userDoc,
  productId,
  selectedDate,
}) => {
  const [selectedTrack, setSelectedTrack] = useState(null);
  const [showLoading, setShowLoading] = useState(false);
  const [trackDuration, setTrackDuration] = useState(null);
  const [programOptions, setProgramOptions] = useState([]);
  const [selectedProgramIndex, setSelectedProgramIndex] = useState(0);
  const [isScheduled, setIsScheduled] = useState(true);
  // The feed card's product, else the panel's — and only when it was opened for this client.
  const { productIdForUser } = useContext(QAPContext);
  const [selectedProductId, setSelectedProductId] = useState(productId ?? productIdForUser(userDoc?.id));

  const { externalCoachDoc } = useContext(ExternalCoachContext);
  const { coachTracks, isReady: isTracksReady } = useContext(ManageTrackContext);
  const { authUser } = useSessionStore();

  const coachId = externalCoachDoc?.id || authUser?.uid;

  const { firebase } = useContext(FirebaseContext);
  const isComponentMountedRef = useComponentMounted();
  const { showToast } = useToast();
  const { scheduleTimeLabel } = useScheduleTimeLabel();

  const tracks = useMemo(() => coachTracks.map((doc) => ({
    id: doc.id,
    label: doc.isHyroxTrack ? `${doc.name} ${texts.hyroxLabel}` : doc.name,
    track: doc,
    group: doc.isPublic ? texts.publicTracks : texts.myTracks,
  })), [coachTracks]);

  useEffect(() => {
    setSelectedProgramIndex(0);
    setProgramOptions([]);
  }, [selectedTrack]);

  /*
    The feed keeps this mounted between openings, so every opening re-seeds — including to null.
    Seeding only when a seed exists would leave the previous client's choice preselected, and the
    selector clears a stale id only when the new client does NOT hold that product.
  */
  useEffect(() => {
    if (!showModal) {
      return;
    }
    setSelectedProductId(productId ?? productIdForUser(userDoc?.id) ?? null);
  }, [showModal, productId, productIdForUser, userDoc]);

  useEffect(() => {
    const calculateDuration = async () => {
      if (!selectedTrack) {
        setTrackDuration(null);
        return;
      }

      setShowLoading(true);
      try {
        const programs = selectedTrack.track.programs || [];
        const programCache = {};

        const refsToFetch = new Set();
        programs.forEach((p) => {
          if (p.programRef && (!p.programName || !p.workoutCount)) {
            refsToFetch.add(p.programRef);
          }
        });

        if (refsToFetch.size > 0) {
          const promises = [];
          refsToFetch.forEach((ref) => {
            promises.push(
              Program.getProgramById(ref).then((doc) => { programCache[ref] = doc; }),
            );
          });
          await Promise.all(promises);
        }

        // Build options using denormalized data or fetched fallback
        const fetchedPrograms = programs.reduce((acc, programItem, index) => {
          if (!programItem.programRef) return acc;
          const cached = programCache[programItem.programRef];
          const programName = programItem.programName ?? cached?.name ?? null;
          const workoutCount = programItem.workoutCount ?? cached?.workouts?.length ?? 0;
          acc.push({
            index,
            label: programName ? `${index + 1}. ${programName}` : `Program ${index + 1}`,
            workoutCount,
          });
          return acc;
        }, []);

        if (isComponentMountedRef.current) {
          setProgramOptions(fetchedPrograms);

          const totalDays = fetchedPrograms.reduce((sum, p) => sum + p.workoutCount, 0);
          setTrackDuration(totalDays);
        }
      } catch (error) {
        Sentry.captureException(error);
      } finally {
        if (isComponentMountedRef.current) {
          setShowLoading(false);
        }
      }
    };

    calculateDuration();
  }, [selectedTrack, isComponentMountedRef]);

  const durationFromSelectedProgram = useMemo(() => {
    if (!programOptions.length) {
      return trackDuration;
    }
    return programOptions
      .filter((p) => p.index >= selectedProgramIndex)
      .reduce((sum, p) => sum + p.workoutCount, 0);
  }, [programOptions, selectedProgramIndex, trackDuration]);

  const endDate = useMemo(() => {
    if (!durationFromSelectedProgram) {
      return null;
    }
    return moment(selectedDate)
      .add(durationFromSelectedProgram - 1, 'days')
      .format(DateFormat.SHORT_MONTH_DAY_COMMA_YEAR_FORMAT);
  }, [selectedDate, durationFromSelectedProgram]);

  const onAssignTrack = useCallback(async () => {
    if (!selectedTrack || !userDoc || !coachId) {
      return;
    }

    setShowLoading(true);
    try {
      const response = await firebase.remote('workout-scheduleAssignmentEndpoint', {
        type: ScheduledAssignmentType.TRACK,
        userId: userDoc.id,
        coachId,
        displayName: selectedTrack.label,
        isScheduled,
        skipLLMCheck: false,
        payload: {
          trackId: selectedTrack.id,
          startingProgramIndex: selectedProgramIndex,
          ...(selectedDate && { startDate: selectedDate }),
        },
        ...(selectedProductId && { productId: selectedProductId }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error assigning track');
      }

      if (isComponentMountedRef.current) {
        showToast(isScheduled ? texts.trackScheduled : texts.trackAssigned);
      }

      if (isComponentMountedRef.current) {
        setShowLoading(false);
        setSelectedTrack(null);
        onClose();
      }
    } catch (error) {
      Sentry.captureException(error);
      if (isComponentMountedRef.current) {
        showToast(texts.errorAssigningTrack, { error: true });
        setShowLoading(false);
      }
    }
  }, [
    coachId,
    isScheduled,
    selectedProductId,
    userDoc,
    selectedTrack,
    selectedProgramIndex,
    selectedDate,
    onClose,
    showToast,
    isComponentMountedRef,
    firebase,
  ]);

  const isMissingProduct = !!userDoc?.hasMultipleActiveProducts && !selectedProductId;

  return (
    <DialogRoundedModal
      title={texts.title}
      description={format(texts.weekDescription, {
        weekStart: moment(selectedDate).startOf('isoWeek').format(DateFormat.SHORT_MONTH_DAY_COMMA_YEAR_FORMAT),
      })}
      fullWidth
      maxWidth="sm"
      actionButtons={(
        <ColumnContainer>
          <CheckBoxContainer>
            <Checkbox
              checked={isScheduled}
              onChange={() => setIsScheduled((prev) => !prev)}
              size="small"
            />
            <StyledCheckBoxLabel>{scheduleTimeLabel}</StyledCheckBoxLabel>
          </CheckBoxContainer>
          <RowContainer>
            <SaveButton
              disabled={!selectedTrack || isMissingProduct}
              icon={<CheckCircleOutline />}
              onClick={onAssignTrack}
            >
              {texts.assignTrack}
            </SaveButton>
            <CancelButton onClick={onClose}>{texts.cancel}</CancelButton>
          </RowContainer>
        </ColumnContainer>
      )}
      open={showModal}
      onClose={onClose}
    >
      <AssignmentProductSelector
        userDoc={userDoc}
        selectedProductId={selectedProductId}
        onSelect={setSelectedProductId}
      />
      <DialogBody>
        <StyledAutocomplete
          disablePortal
          value={selectedTrack}
          options={tracks}
          groupBy={(option) => option.group || texts.myTracks}
          renderInput={(params) => <TextField {...params} label={texts.selectTrack} />}
          onChange={(event, value) => setSelectedTrack(value)}
          renderOption={(props, { id, label }) => (
            <StyledOption {...props} key={id}>
              {label}
            </StyledOption>
          )}
        />
        {selectedTrack && programOptions.length > 1 && (
          <StyledAutocomplete
            disablePortal
            value={programOptions.find((p) => p.index === selectedProgramIndex) || null}
            options={programOptions}
            renderInput={(params) => <TextField {...params} label={texts.startingProgram} />}
            onChange={(event, value) => {
              if (value) {
                setSelectedProgramIndex(value.index);
              }
            }}
            renderOption={(props, option) => (
              <StyledOption {...props} key={option.index}>
                {option.label}
              </StyledOption>
            )}
          />
        )}
        {endDate !== null && (
          <SettingSection>
            <StyledInputLabel>{texts.duration}</StyledInputLabel>
            <DurationContainer>
              <StyledDurationText>
                {`${moment(selectedDate).format(DateFormat.SHORT_MONTH_DAY_COMMA_YEAR_FORMAT)} - ${endDate}`}
              </StyledDurationText>
            </DurationContainer>
          </SettingSection>
        )}
      </DialogBody>
      <LoadingOverlay isLoading={showLoading || !isTracksReady} />
    </DialogRoundedModal>
  );
};

TrackAssignmentModal.propTypes = {
  showModal: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  // A shape, not `instanceOf(User)`: the feed falls back to `{ id }` while the doc loads.
  userDoc: PropTypes.shape({
    id: PropTypes.string,
    hasMultipleActiveProducts: PropTypes.bool,
    activeProductIds: PropTypes.arrayOf(PropTypes.string),
  }),
  productId: PropTypes.string,
  selectedDate: PropTypes.string,
};

TrackAssignmentModal.defaultProps = {
  userDoc: null,
  productId: undefined,
  selectedDate: moment().format(DateFormat.SHORT_MONTH_DAY_COMMA_YEAR_FORMAT),
};

export default compose(
  observer,
)(TrackAssignmentModal);
